<!-- No scroll anchoring: pTextarea's autoResize briefly sets every textarea to height: auto on each
     change detection, and anchoring then scrolls the page on mousedown, so a click below the
     textareas ("Add variable", "Add example") landed beside its button and did nothing. -->
<div class="p-6 max-w-5xl mx-auto [overflow-anchor:none]">
  <!-- The tab bar already shows this label; the heading is for screen readers and structure. -->
  <h1 class="sr-only">Support email</h1>

  <div class="flex flex-row flex-wrap items-start justify-between gap-4 mb-6">
    <div class="max-w-2xl">
      <p class="text-sm text-slate-700">
        Each incoming support email gets one category. The classifier picks it using
        <span class="font-medium">When to use</span> and the <span class="font-medium">example emails</span>.
        That category's <span class="font-medium">reply template</span> is then saved as a Zoho draft for a
        person to review; emails that fit no category go to <span class="font-medium">Other</span> and get
        no draft. Add a category for each kind of question you want drafted. Templates can use the
        <span class="font-medium">variables</span> defined at the bottom of this page.
      </p>
      <p class="text-sm text-slate-700 mt-2">
        For emails that came in on an earlier day, use
        <a
          routerLink="past-emails"
          class="text-blue-600 no-underline hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >Past emails</a
        >.
      </p>
      @let current = settings();
      @if (current?.updatedAt; as updatedAt) {
        <p class="text-xs text-muted-color mt-1">
          Last saved {{ updatedAt | date: dateTimeFormat }} by {{ current?.updatedBy ?? 'unknown' }}
        </p>
      } @else if (current) {
        <p class="text-xs text-muted-color mt-1">Not edited yet: these are the built-in defaults.</p>
      }
    </div>
    <div class="flex flex-col items-end gap-1 max-w-60">
      <homey-submit-button
        label="Save all"
        [submitFunction]="save"
        [disabled]="loading() || categories().length === 0 || sectionsWithProblems() > 0"
      />
      <!-- Save all sits at the top of a long page, so say why it is disabled: the red lines
           explaining it can be several cards below. -->
      @let problemCount = sectionsWithProblems();
      @if (problemCount > 0) {
        <p class="text-xs text-red-600 text-right" aria-live="polite">
          {{ problemCount === 1 ? '1 section has' : problemCount + ' sections have' }} a problem to fix, marked in red below.
        </p>
      }
    </div>
  </div>

  @if (loading()) {
    <p class="text-sm text-muted-color">Loading…</p>
  } @else if (categories().length === 0) {
    <p class="text-sm text-muted-color">Support email settings could not be loaded.</p>
  } @else {
    @for (category of categories(); track $index; let index = $index) {
      @let name = category.title || 'New category';
      <section class="rounded-lg border border-slate-200 p-4 mb-6" [attr.aria-labelledby]="'category-' + index">
        <h2 [id]="'category-' + index" class="sr-only">{{ name }}</h2>
        <div class="flex items-end gap-3">
          <div class="flex-1">
            <label [for]="'title-' + index" class="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input
              pInputText
              [id]="'title-' + index"
              [ngModel]="category.title"
              (ngModelChange)="setTitle(index, $event)"
              class="w-full text-base font-semibold"
            />
          </div>
          <p-button
            icon="pi pi-trash"
            severity="secondary"
            [text]="true"
            [disabled]="categories().length === 1"
            [ariaLabel]="'Remove ' + name"
            (onClick)="removeCategory(index)"
          />
        </div>
        @if (category.label) {
          <p class="text-xs font-mono text-muted-color mt-1 mb-4">{{ category.label }}</p>
        } @else {
          <p class="text-xs text-muted-color mt-1 mb-4">New: its id is made from the title when you save.</p>
        }

        <label [for]="'when-' + index" class="block text-sm font-medium text-slate-700 mb-1">When to use</label>
        <textarea
          pTextarea
          [id]="'when-' + index"
          [value]="category.whenToUse"
          (input)="setWhenToUse(index, $event)"
          [autoResize]="true"
          rows="2"
          class="w-full text-sm"
        ></textarea>

        <div class="mt-4">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-slate-700">
              Example emails ({{ category.examples.length }}/{{ maxExamples }})
            </span>
            <p-button
              label="Add example"
              icon="pi pi-plus"
              size="small"
              severity="secondary"
              [text]="true"
              [disabled]="category.examples.length >= maxExamples"
              (onClick)="addExample(index)"
            />
          </div>
          @for (example of category.examples; track $index) {
            <div class="flex items-start gap-2 mb-2">
              <textarea
                pTextarea
                [value]="example"
                (input)="setExample(index, $index, $event)"
                [autoResize]="true"
                [attr.aria-label]="name + ' example ' + ($index + 1)"
                rows="3"
                class="w-full text-sm"
              ></textarea>
              <p-button
                icon="pi pi-trash"
                severity="secondary"
                [text]="true"
                [ariaLabel]="'Remove example ' + ($index + 1)"
                (onClick)="removeExample(index, $index)"
              />
            </div>
          } @empty {
            <p class="text-sm text-muted-color">No examples yet.</p>
          }
        </div>

        <label [for]="'template-' + index" class="block text-sm font-medium text-slate-700 mt-4 mb-1">
          Reply template
        </label>
        <textarea
          pTextarea
          [id]="'template-' + index"
          [value]="category.template"
          (input)="setTemplate(index, $event)"
          [autoResize]="true"
          rows="6"
          class="w-full text-sm font-mono"
        ></textarea>

        @for (problem of problemsByCategory()[index]; track problem) {
          <p class="text-sm text-red-600 mt-2">{{ problem }}</p>
        }
      </section>
    }
    <div class="mb-6 -mt-3">
      <p-button
        label="Add category"
        icon="pi pi-plus"
        size="small"
        severity="secondary"
        [text]="true"
        [disabled]="categories().length >= maxCategories"
        (onClick)="addCategory()"
      />
    </div>

    <section aria-labelledby="other-heading" class="rounded-lg border border-slate-200 p-4 mb-6">
      <h2 id="other-heading" class="text-base font-semibold text-slate-800">Other</h2>
      <p class="text-xs font-mono text-muted-color mb-1">other</p>
      <p class="text-sm text-muted-color mb-4">Emails that fit no category get no draft: a person replies.</p>
      <label for="when-other" class="block text-sm font-medium text-slate-700 mb-1">When to use</label>
      <textarea
        pTextarea
        id="when-other"
        [value]="otherWhenToUse()"
        (input)="setOtherWhenToUse($event)"
        [autoResize]="true"
        rows="2"
        class="w-full text-sm"
      ></textarea>
      @for (problem of otherProblems(); track problem) {
        <p class="text-sm text-red-600 mt-2">{{ problem }}</p>
      }
    </section>

    <section aria-labelledby="variables-heading" class="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-6">
      <div class="flex items-center justify-between mb-1">
        <h2 id="variables-heading" class="text-base font-semibold text-slate-800">Variables</h2>
        <p-button
          label="Add variable"
          icon="pi pi-plus"
          size="small"
          severity="secondary"
          [text]="true"
          [disabled]="variables().length >= maxVariables"
          (onClick)="addVariable()"
        />
      </div>
      <p class="text-sm text-slate-700 mb-4 max-w-3xl">
        Use a variable in a reply template as <span class="font-mono">{{ '{name}' }}</span>. For each email it takes
        the first case, top to bottom, whose condition fits the sender; the last case is
        <span class="font-medium">Always</span>. A case marked <span class="font-medium">No draft</span> means that
        email gets no draft and a person replies.
      </p>

      @for (variable of variables(); track $index; let variableIndex = $index) {
        <div
          class="rounded-md border border-slate-200 bg-white p-4 mb-4"
          role="group"
          [attr.aria-label]="'Variable ' + '{' + variable.name + '}'"
          [attr.data-variable]="variable.name"
        >
          <div class="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label [for]="'variable-name-' + variableIndex" class="block text-sm font-medium text-slate-700 mb-1">
                Name
              </label>
              <div class="flex items-center gap-1 font-mono text-sm">
                <span aria-hidden="true">{{ '{' }}</span>
                <input
                  pInputText
                  [id]="'variable-name-' + variableIndex"
                  [ngModel]="variable.name"
                  (ngModelChange)="setVariableName(variableIndex, $event)"
                  placeholder="name"
                  class="font-mono text-sm w-48"
                />
                <span aria-hidden="true">{{ '}' }}</span>
              </div>
            </div>
            <div class="flex-1 min-w-60">
              <label
                [for]="'variable-description-' + variableIndex"
                class="block text-sm font-medium text-slate-700 mb-1"
              >
                Description
              </label>
              <input
                pInputText
                [id]="'variable-description-' + variableIndex"
                [ngModel]="variable.description"
                (ngModelChange)="setVariableDescription(variableIndex, $event)"
                class="w-full text-sm"
              />
            </div>
            <p-button
              icon="pi pi-trash"
              severity="secondary"
              [text]="true"
              [ariaLabel]="'Remove variable ' + '{' + variable.name + '}'"
              (onClick)="removeVariable(variableIndex)"
            />
          </div>

          <ol class="grid gap-3 list-decimal pl-5">
            @for (variableCase of variable.cases; track $index; let caseIndex = $index; let first = $first; let last = $last) {
              <li class="rounded border border-slate-200 p-3">
                <div class="flex flex-wrap items-center gap-3 mb-2">
                  <p-select
                    [options]="conditions()"
                    optionLabel="label"
                    optionValue="condition"
                    [ngModel]="variableCase.when"
                    (ngModelChange)="setCaseCondition(variableIndex, caseIndex, $event)"
                    [ariaLabel]="'Condition of case ' + (caseIndex + 1)"
                    appendTo="body"
                    styleClass="w-96"
                  />
                  <div class="flex items-center gap-2">
                    <p-checkbox
                      [binary]="true"
                      [inputId]="'no-draft-' + variableIndex + '-' + caseIndex"
                      [ngModel]="variableCase.noDraft"
                      (ngModelChange)="setCaseNoDraft(variableIndex, caseIndex, $event)"
                    />
                    <label [for]="'no-draft-' + variableIndex + '-' + caseIndex" class="text-sm text-slate-700">
                      No draft
                    </label>
                  </div>
                  <span class="flex-1"></span>
                  <p-button
                    icon="pi pi-arrow-up"
                    severity="secondary"
                    size="small"
                    [text]="true"
                    [disabled]="first"
                    [ariaLabel]="'Move case ' + (caseIndex + 1) + ' up'"
                    (onClick)="moveCase(variableIndex, caseIndex, -1)"
                  />
                  <p-button
                    icon="pi pi-arrow-down"
                    severity="secondary"
                    size="small"
                    [text]="true"
                    [disabled]="last"
                    [ariaLabel]="'Move case ' + (caseIndex + 1) + ' down'"
                    (onClick)="moveCase(variableIndex, caseIndex, 1)"
                  />
                  <p-button
                    icon="pi pi-trash"
                    severity="secondary"
                    size="small"
                    [text]="true"
                    [disabled]="variable.cases.length === 1"
                    [ariaLabel]="'Remove case ' + (caseIndex + 1)"
                    (onClick)="removeCase(variableIndex, caseIndex)"
                  />
                </div>
                @if (variableCase.noDraft) {
                  <p class="text-sm text-muted-color">No draft for this sender: a person replies.</p>
                } @else {
                  <textarea
                    pTextarea
                    [value]="variableCase.text"
                    (input)="setCaseText(variableIndex, caseIndex, $event)"
                    [autoResize]="true"
                    rows="1"
                    [attr.aria-label]="'Text of case ' + (caseIndex + 1)"
                    class="w-full text-sm"
                  ></textarea>
                  @let fields = fieldsByCondition().get(variableCase.when) ?? [];
                  @if (fields.length > 0) {
                    <p class="text-xs text-muted-color mt-1">Can use {{ fields.join(', ') }}.</p>
                  }
                }
              </li>
            }
          </ol>
          <p-button
            label="Add case"
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            [text]="true"
            [disabled]="variable.cases.length >= maxCases"
            (onClick)="addCase(variableIndex)"
            styleClass="mt-2"
          />

          @for (problem of problemsByVariable()[variableIndex]; track problem) {
            <p class="text-sm text-red-600 mt-2">{{ problem }}</p>
          }
        </div>
      } @empty {
        <p class="text-sm text-muted-color">No variables yet: templates are used as written.</p>
      }
    </section>
  }
</div>
